import { useCallback, useEffect, useRef, useState } from "react";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getValoresAtributo, grabarValorAtributo, eliminarValorAtributo } from "../../../services/backend/catalogService";
import useActionState from "./useActionState";
import useDialogState from "./useDialogState";

const useValoresAtributo = ({ atributo, onError, onToast }) => {
    const onErrorRef = useRef(onError);
    const onToastRef = useRef(onToast);
    onErrorRef.current = onError;
    onToastRef.current = onToast;

    const [valores, setValores] = useState([]);
    const [loading, setLoading] = useState(false);

    const agregarDialog = useDialogState();
    const editarDialog = useDialogState();
    const eliminarDialog = useDialogState();

    const { loading: saving, run } = useActionState({
        onError: (err) => onErrorRef.current?.(err),
    });

    const fetchValores = useCallback(async (atributoId) => {
        if (!atributoId) {
            setValores([]);
            return;
        }
        setLoading(true);
        try {
            await ensureCatalogAuth();
            const response = await getValoresAtributo(atributoId);
            const items = Array.isArray(response?.valores) ? response.valores
                : Array.isArray(response) ? response : [];
            setValores(items);
        } catch (err) {
            setValores([]);
            onErrorRef.current?.(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (atributo?.id) {
            fetchValores(atributo.id);
        } else {
            setValores([]);
        }
    }, [atributo?.id, fetchValores]);

    const refetch = () => fetchValores(atributo?.id);

    const handleAgregar = async (data) => {
        const ok = await run(async () => {
            await grabarValorAtributo({
                id_atributo: atributo?.id,
                valor: data.valor,
            });
        });
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Valor creado", life: 2500 });
            agregarDialog.close();
            refetch();
        }
        return ok;
    };

    const handleEditar = async (data) => {
        const ok = await run(async () => {
            await grabarValorAtributo({
                id: data.id,
                id_atributo: atributo?.id,
                valor: data.valor,
            });
        });
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Valor actualizado", life: 2500 });
            editarDialog.close();
            refetch();
        }
        return ok;
    };

    const handleEliminar = async () => {
        const target = eliminarDialog.selectedItem;
        if (!target?.id) return;
        const ok = await run(async () => {
            await eliminarValorAtributo(target.id);
        });
        eliminarDialog.close();
        if (ok) {
            onToastRef.current?.({ severity: "success", summary: "Valor eliminado", life: 2500 });
            refetch();
        }
    };

    return {
        valores,
        loading,
        saving,
        eliminarDialog,
        handleAgregar,
        handleEditar,
        handleEliminar,
    };
};

export default useValoresAtributo;
